import { Stack, Chip } from '@mui/material';
import {
  GridColDef,
  GridSortModel,
  getGridStringOperators,
  getGridSingleSelectOperators,
  GridFilterOperator,
} from '@mui/x-data-grid';
import {
  entityType,
  ItemRelationshipEntityType,
  itemRelationshipEntityTypes,
  itemStatus,
  ItemStatus,
} from '@nohara-dxdev/mt-common';
import { permission } from '@nohara-dxdev/oidc-core';
import { AdvancedFilterModel, formatDateTime, PaginatedDataGrid } from '@nohara-dxdev/react-core';
import { OidcProtectedComponent, useScopeIds } from '@nohara-dxdev/react-oidc';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSettingValues } from '@/features/settings';
import { TasksButton } from '@/features/tasks';
import { ItemEntity, SettingIdValue } from '@/types/api';

import { useItems } from '../api/get-items';
import {
  itemSortableFields,
  ItemSortableField,
  ItemSortOrder,
  useItemFilterValues,
} from '../providers';
import { getStatusColor } from '../utils/get-item-status-color';
import { getStatusLabel } from '../utils/get-item-status-label';

import { CloneItemDialog } from './clone-item-dialog';
import { DeleteItem } from './delete-item';
import { EditItemButton } from './edit-item-button';
import { OpenInRevitButton } from './open-in-revit-button';
import { REVIT_URI_SCHEME } from './revit-deep-link-constants';
import { ViewItemButton } from './view-item-button';

const updateItemPermissions = [permission.item.update];
const cloneItemPermissions = [permission.item.create];
const deleteItemPermissions = [permission.item.delete];

// Default sort configuration
const DEFAULT_SORT_FIELD: ItemSortableField = 'createdAt';
const DEFAULT_SORT_ORDER: ItemSortOrder = 'desc';

const filterOperatorTranslationMap = {
  equals: 'common.filterOperators.equals',
  is: 'common.filterOperators.equals',
  doesNotEqual: 'common.filterOperators.notEquals',
  not: 'common.filterOperators.notEquals',
  contains: 'common.filterOperators.contains',
  doesNotContain: 'common.filterOperators.notContains',
  startsWith: 'common.filterOperators.startsWith',
  endsWith: 'common.filterOperators.endsWith',
  isAnyOf: 'common.filterOperators.isAnyOf',
  isEmpty: 'common.filterOperators.isEmpty',
  isNotEmpty: 'common.filterOperators.isNotEmpty',
};

interface ItemsListProps {
  additionalColumns?: GridColDef<ItemEntity>[];
  additionalRelationships?: ItemRelationshipEntityType[];
}

export const ItemsList = ({ additionalColumns, additionalRelationships }: ItemsListProps = {}) => {
  const filterValues = useItemFilterValues();
  const subject = useScopeIds();
  const { t } = useTranslation();
  const { settings, getSettingValue } = useSettingValues();

  // Server-side sorting state
  const [sortField, setSortField] = useState<ItemSortableField | undefined>(DEFAULT_SORT_FIELD);
  const [sortOrder, setSortOrder] = useState<ItemSortOrder | undefined>(DEFAULT_SORT_ORDER);

  // Advanced filter state
  // pendingAdvancedFilterModel: what the user is editing in the filter panel
  // appliedAdvancedFilterModel: what's actually applied to the query
  const [pendingAdvancedFilterModel, setPendingAdvancedFilterModel] = useState<AdvancedFilterModel>(
    {
      conditions: [],
    },
  );
  const [appliedAdvancedFilterModel, setAppliedAdvancedFilterModel] = useState<AdvancedFilterModel>(
    {
      conditions: [],
    },
  );

  // Combine advancedFilterModel from context with applied advanced filter model
  const combinedAdvancedFilterModel = useMemo(() => {
    const contextAdvancedFilterModel = filterValues.advancedFilterModel;

    // If we have an applied advanced filter model, use it
    // Otherwise, use the context one if available
    if (appliedAdvancedFilterModel.conditions.length > 0) {
      return appliedAdvancedFilterModel;
    }

    return contextAdvancedFilterModel ?? { conditions: [] };
  }, [appliedAdvancedFilterModel, filterValues]);

  const itemsQuery = useItems({
    advancedFilterModel:
      combinedAdvancedFilterModel.conditions.length > 0 ? combinedAdvancedFilterModel : undefined,
    filterModel: filterValues.filterModel, // Keep for backward compatibility
    locationIdOrAdjacentLocationIdEq: filterValues.locationIdOrAdjacentLocationIdEq,
    isUnassignedToLocation: filterValues.isUnassignedToLocation,
    isUnassignedToSystem: filterValues.isUnassignedToSystem,
    infrastructureRelationshipRequirements: filterValues.infrastructureRelationshipRequirements,
    sortField,
    sortOrder,
    relationships: [
      itemRelationshipEntityTypes.itemTemplate,
      itemRelationshipEntityTypes.circuits,
      itemRelationshipEntityTypes.infrastructures,
      ...(additionalRelationships ?? []),
    ],
  });

  // Convert state to MUI DataGrid sortModel format
  const sortModel = useMemo<GridSortModel>(() => {
    if (!sortField) {
      return [];
    }
    return [{ field: sortField, sort: sortOrder }];
  }, [sortField, sortOrder]);

  // Handle sort model changes from DataGrid
  const handleSortModelChange = useCallback((model: GridSortModel) => {
    if (model.length === 0) {
      // Reset to default when sorting is cleared
      setSortField(undefined);
      setSortOrder(undefined);
      return;
    }

    const { field, sort } = model[0];

    // Only allow sorting on fields that exist in the backend
    if (itemSortableFields.includes(field as ItemSortableField)) {
      setSortField(field as ItemSortableField);
      setSortOrder((sort ?? 'asc') as ItemSortOrder);
    }
  }, []);

  // Memoized helper function to translate filter operators
  const translateFilterOperators = useCallback(
    (operators: GridFilterOperator[], allowedOperators?: string[]): GridFilterOperator[] => {
      return operators
        .filter((operator) => !allowedOperators || allowedOperators.includes(operator.value))
        .map((operator) => {
          const translationKey =
            filterOperatorTranslationMap[
              operator.value as keyof typeof filterOperatorTranslationMap
            ];
          const defaultLabel = operator.label ?? '';
          return {
            ...operator,
            label: translationKey
              ? t(translationKey, { defaultValue: defaultLabel })
              : defaultLabel,
          };
        });
    },
    [t],
  );

  // Memoized string filter operators (for itemNumber and name columns)
  const stringFilterOperators = useMemo(() => {
    const operators = getGridStringOperators().filter((operator) => {
      return [
        'equals',
        'doesNotEqual',
        'contains',
        'doesNotContain',
        'startsWith',
        'endsWith',
        'isAnyOf',
      ].includes(operator.value);
    });
    return translateFilterOperators(operators) as GridFilterOperator<ItemEntity, string, unknown>[];
  }, [translateFilterOperators]);

  // Memoized singleSelect filter operators (for status column)
  const singleSelectFilterOperators = useMemo(() => {
    const operators = getGridSingleSelectOperators().filter((operator) => {
      return ['isAnyOf', 'is', 'not'].includes(operator.value);
    });
    return translateFilterOperators(operators) as GridFilterOperator<ItemEntity, string, unknown>[];
  }, [translateFilterOperators]);

  const emptySelectFilterOperators = useMemo(() => {
    const operators = getGridStringOperators().filter((operator) => {
      return ['isEmpty', 'isNotEmpty'].includes(operator.value);
    });
    return translateFilterOperators(operators) as GridFilterOperator<ItemEntity, string, unknown>[];
  }, [translateFilterOperators]);

  // Locked is a two-value boolean, so `isAnyOf` is meaningless (matches everything)
  // and unsupported by the backend `[EQ, NEQ]` allow-list — offer only Is / Is not.
  const lockedFilterOperators = useMemo(() => {
    const operators = getGridSingleSelectOperators().filter((operator) => {
      return ['is', 'not'].includes(operator.value);
    });
    return translateFilterOperators(operators) as GridFilterOperator<ItemEntity, string, unknown>[];
  }, [translateFilterOperators]);

  // `settings` is the dep (not just getSettingValue): getSettingValue is
  // ref-stable, so without it these memos never recompute once settings load.
  const itemTypeValueOptions = useMemo(() => {
    return getSettingValue<SettingIdValue<string>[]>('itemTypes', []).map(({ id, value }) => ({
      value: id,
      label: value,
    }));
  }, [getSettingValue, settings]);

  const locationTypeValueOptions = useMemo(() => {
    return getSettingValue<SettingIdValue<string>[]>('locationTypes', []).map(({ id, value }) => ({
      value: id,
      label: value,
    }));
  }, [getSettingValue, settings]);

  const columns = useMemo(
    (): GridColDef<ItemEntity>[] => [
      {
        field: 'itemNumber',
        headerName: t('items.list.columns.itemNumber'),
        flex: 1,
        sortable: true,
        filterable: true,
        filterOperators: stringFilterOperators,
      },
      {
        field: 'name',
        headerName: t('items.list.columns.name'),
        flex: 1.5,
        sortable: true,
        filterable: true,
        filterOperators: stringFilterOperators,
      },
      ...(additionalColumns ?? []),
      {
        field: 'itemTypeId',
        headerName: t('items.list.columns.itemType'),
        flex: 1,
        sortable: false,
        filterable: true,
        type: 'singleSelect',
        valueOptions: itemTypeValueOptions,
        filterOperators: singleSelectFilterOperators,
      },
      {
        field: 'locationTypeId',
        headerName: t('items.list.columns.locationType'),
        flex: 1,
        sortable: false,
        filterable: true,
        type: 'singleSelect',
        valueOptions: locationTypeValueOptions,
        filterOperators: [...singleSelectFilterOperators, ...emptySelectFilterOperators],
      },
      {
        field: 'status',
        headerName: t('items.list.columns.decisionStatus'),
        flex: 1,
        sortable: false,
        filterable: true,
        type: 'singleSelect',
        valueOptions: Object.values(itemStatus).map((status) => ({
          value: status,
          label: getStatusLabel(status as ItemStatus, t),
        })),
        filterOperators: singleSelectFilterOperators,
        renderCell: (params) => {
          if (params.row.locked) {
            return (
              <Chip
                label={t('items.status.locked', 'Locked')}
                size="small"
                color="error"
                variant="outlined"
              />
            );
          }
          return (
            <Chip
              label={getStatusLabel(params.value as ItemStatus, t)}
              size="small"
              color={getStatusColor(params.value as ItemStatus)}
              variant="outlined"
            />
          );
        },
      },
      {
        field: 'locked',
        headerName: t('items.list.columns.locked'),
        sortable: false,
        filterable: true,
        type: 'singleSelect',
        valueOptions: [
          { value: true, label: t('items.status.locked') },
          { value: false, label: t('items.status.unlocked') },
        ],
        filterOperators: lockedFilterOperators,
      },
      {
        field: 'createdAt',
        headerName: t('items.list.columns.createdAt'),
        flex: 0.8,
        sortable: true,
        filterable: true,
        type: 'dateTime',
        valueFormatter: (value: number) => formatDateTime(value),
      },
      {
        field: 'actions',
        headerName: t('common.actions', 'Actions'),
        flex: 1.1,
        // Keep all four action icons visible at any viewport width — without
        // a floor the flex column shrank until the icons clipped (MT1-1358).
        minWidth: 240,
        sortable: false,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          return (
            <>
              <OidcProtectedComponent
                requireAll={updateItemPermissions}
                subject={{ ...subject }}
                context={{
                  itemTemplate: { projectRoleTags: params.row.itemTemplate?.projectRoleTags },
                }}
                fallbackElement={<ViewItemButton item={params.row} />}
              >
                <EditItemButton item={params.row} />
              </OidcProtectedComponent>
              <TasksButton
                entityId={params.row.id}
                entityType={entityType.item}
                entityName={params.row.name}
              />
              <OidcProtectedComponent
                requireAll={cloneItemPermissions}
                subject={{ ...subject }}
                context={{
                  itemTemplate: { projectRoleTags: params.row.itemTemplate?.projectRoleTags },
                }}
              >
                <CloneItemDialog itemId={params.row.id} itemName={params.row.name} />
              </OidcProtectedComponent>
              <OidcProtectedComponent
                requireAll={deleteItemPermissions}
                subject={{ ...subject }}
                context={{
                  itemTemplate: { projectRoleTags: params.row.itemTemplate?.projectRoleTags },
                }}
              >
                <DeleteItem itemId={params.row.id} />
              </OidcProtectedComponent>
              {(() => {
                const revitId = params.row.revitFileId ?? params.row.revitProjectId;
                return revitId && params.row.revitUniqueId ? (
                  <OpenInRevitButton
                    uri={`${REVIT_URI_SCHEME}open?projectId=${encodeURIComponent(revitId)}&uniqueId=${encodeURIComponent(params.row.revitUniqueId)}`}
                  />
                ) : null;
              })()}
            </>
          );
        },
      },
    ],
    [
      t,
      subject,
      stringFilterOperators,
      singleSelectFilterOperators,
      emptySelectFilterOperators,
      lockedFilterOperators,
      itemTypeValueOptions,
      locationTypeValueOptions,
      additionalColumns,
    ],
  );

  // Handle advanced filter model changes from filter panel (only updates pending filter, not applied)
  const handleAdvancedFilterModelChange = useCallback((model: AdvancedFilterModel) => {
    setPendingAdvancedFilterModel(model);
  }, []);

  // Apply the pending advanced filter model (called from filter panel)
  const handleAdvancedFilterApply = useCallback((model: AdvancedFilterModel) => {
    setAppliedAdvancedFilterModel(model);
    setPendingAdvancedFilterModel(model);
  }, []);

  // Clear all filters (called from filter panel)
  const handleAdvancedFilterClear = useCallback(() => {
    const emptyFilterModel: AdvancedFilterModel = { conditions: [] };
    setPendingAdvancedFilterModel(emptyFilterModel);
    setAppliedAdvancedFilterModel(emptyFilterModel);
  }, []);

  return (
    <Stack spacing={3} mt={2}>
      <PaginatedDataGrid
        paginatedQuery={itemsQuery}
        columns={columns}
        initialState={{
          columns: {
            columnVisibilityModel: {
              locked: false,
            },
          },
        }}
        showTopPagination
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        sortingMode="server"
        enableAdvancedFilters={true}
        advancedFilterModel={pendingAdvancedFilterModel}
        onAdvancedFilterModelChange={handleAdvancedFilterModelChange}
        onAdvancedFilterApply={handleAdvancedFilterApply}
        onAdvancedFilterClear={handleAdvancedFilterClear}
        sx={{
          '--DataGrid-overlayHeight': '300px',
          '& .MuiDataGrid-cell': {
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiDataGrid-row': {
            minHeight: '60px',
          },
          '.MuiDataGrid-columnHeader--sorted .MuiDataGrid-sortIcon': {
            opacity: 1,
            color: 'secondary.main',
          },
        }}
      />
    </Stack>
  );
};
